// SupportRuntime — the injected I/O contract for the shared Support module UI.
//
// Same split as `shell/worklist/contract.ts`: the platform Run view, the
// builder workbench, the downloaded runtime and the J2EE runtime all render the
// SAME components and differ only in how these calls reach a server. The
// platform binds them to `/api/support/{project}/...`; a deployed runtime
// proxies the reporter half (and the update feed) to the platform's intake.
//
// The shapes mirror the backend serializers — `sdlc/store.py`
// (`issue_summary`, `issue_detail`, `analysis_dict`, `build_dict`),
// `sdlc/state_machine.meta()`, `sdlc/releases.release_dict`,
// `sdlc/distribution` (`org_status`, `preflight`, `changes_between`) and
// `sdlc/roles.SupportCaller.to_dict`. The LIFECYCLE is data
// (`SupportMeta.lifecycle`): states, board columns and events are rendered from
// it, never from a copy kept here.

// ── who is asking ──────────────────────────────────────────────────────────

/** `SupportCaller.to_dict` — the caller's support identity on one application. */
export interface SupportMe {
  user_id: string
  /** `account` (Twasta) · `project_user` (registry) · `runtime_user` (deployed). */
  kind: string
  email?: string | null
  name?: string | null
  tenant_id?: string | null
  /** Support capacities: any of `dev` · `qc` · `pm`. */
  capacities: string[]
  /** Holds a vendor-side support capacity (sees the board). */
  is_team: boolean
  /** Belongs to the organization that OWNS the application. */
  is_vendor: boolean
  /** Vendor support team — sees every organization's issues. */
  sees_all_orgs: boolean
  /** Functional administrator of the caller's own organization. */
  is_org_admin: boolean
  /** May open the builder (workbench) for this application. */
  is_author: boolean
}

// ── the lifecycle, as data ─────────────────────────────────────────────────

export interface SupportStateDef {
  id: string
  label: string
  /** The board column (`SupportColumnDef.id`) the state sits in. */
  column: string
}

export interface SupportColumnDef {
  id: string
  label: string
}

export interface SupportEventDef {
  event: string
  label: string
  from: string[]
  to: string
  /** Capacities that may fire it: reporter · dev · qc · pm · agent · system. */
  who: string[]
  needs_comment: boolean
}

export interface SupportLifecycle {
  states: SupportStateDef[]
  columns: SupportColumnDef[]
  terminal: string[]
  events: SupportEventDef[]
}

/** One entry of a policy vocabulary (issue types, severities). */
export interface SupportVocabItem {
  id: string
  label: string
  icon?: string | null
}

/** The option vocabularies the settings screens offer (`public_view.options`). */
export interface SupportPolicyOptions {
  gates?: {
    auto_analyse?: string[]
    plan_approval?: string[]
    qc?: string[]
    pm_approval?: string[]
    [key: string]: string[] | undefined
  }
  release_mode?: string[]
  github_mode?: string[]
  auto_upgrade?: string[]
  note_groups?: string[]
  [key: string]: unknown
}

/** `policy.public_view` — what every signed-in user of the app may read. */
export interface SupportPublicPolicy {
  issue_types: SupportVocabItem[]
  severities: SupportVocabItem[]
  /** Priority ids, most urgent first (older servers omit it). */
  priorities?: string[]
  versioning_enabled: boolean
  /** Option vocabularies for the settings screens (older servers omit it). */
  options?: SupportPolicyOptions
}

/** Badge counts (`store.counts`). */
export interface SupportCounts {
  mine_open: number
  /** Team only: issues sitting in a state the caller's capacity acts on. */
  waiting_on_me: number
  /** My issues the pipeline is waiting for me to answer. */
  needs_info_mine: number
}

/** The environment the caller is looking at (`environment.Binding.to_dict`). */
export interface SupportEnvironment {
  /** `live` (not versioned) · `development` · `staging` · `production`. */
  kind: string
  version?: string | null
  label?: string | null
  issue_number?: number | null
  tenant_id?: string | null
  isolated_db?: boolean
}

export interface SupportMeta {
  me: SupportMe
  lifecycle: SupportLifecycle
  policy: SupportPublicPolicy
  counts: SupportCounts
  environment: SupportEnvironment
  // A deployed runtime (proxying to the platform hub) also says:
  /** The hub could not be reached; the vocabularies are its last answer and
   *  new reports go to the runtime's outbox. */
  offline?: boolean
  offline_reason?: string | null
  /** Reports waiting in the outbox. */
  queued?: number
  deployment?: { runtime?: string; label?: string | null; tenant_id?: string | null; version?: string | null; project?: string }
  /** Administrators of a deployed runtime: a newer release is out. */
  pending_update?: PendingUpdate | null
}

/** The newest release newer than what a deployment runs. */
export interface PendingUpdate {
  version: string
  title?: string | null
  released_at?: string | null
  /** How many releases are newer than the installed one. */
  count?: number
  /** Whether its git tag is pushed (a live server can only pull a pushed tag). */
  pushed?: boolean | null
}

// ── issues ─────────────────────────────────────────────────────────────────

export interface IssueSummary {
  id: number
  number: number
  title: string
  type: string
  severity: string
  priority?: string | null
  status: string
  reporter_name?: string | null
  tenant_id?: string | null
  assignee_id?: string | null
  labels: string[]
  risk?: string | null
  source?: string | null
  affects_release?: string | null
  created_at?: string | null
  updated_at?: string | null
  sla_due_at?: string | null
  sla_overdue?: boolean
}

/** One timeline row (`store.event_dict`). */
export interface IssueEvent {
  id: number
  at?: string | null
  actor_kind?: string | null
  actor_id?: string | null
  actor_name?: string | null
  /** created · transition · comment · ai_note · question · answer · attachment · field · link */
  kind: string
  from?: string | null
  to?: string | null
  body?: string | null
  payload: Record<string, unknown>
  /** `public` · `internal` (team only — never shown to a reporter). */
  visibility?: string
}

export interface IssueAttachment {
  id: number
  filename: string
  mime: string
  size: number
  event_id?: number | null
  created_at?: string | null
}

/** An event the caller may fire now (`state_machine.allowed_events`). */
export interface IssueAction {
  event: string
  label: string
  to: string
  needs_comment: boolean
}

export interface AnalysisEvidence {
  kind?: string
  detail?: string
  [key: string]: unknown
}

export interface AffectedObject {
  obj_name?: string
  obj_type?: string
  why?: string
  [key: string]: unknown
}

/** One step of a change plan (`change_agent.create_change_plan` shape). */
export interface PlanStep {
  id?: string
  label?: string
  artifact_type?: string
  action?: string
  target_file?: string
  obj_name?: string | null
  obj_type?: string | null
  method?: string | null
  instruction?: string
  [key: string]: unknown
}

export interface ChangePlan {
  summary?: string
  plan_summary?: string
  steps?: PlanStep[]
  [key: string]: unknown
}

export interface TestPlan {
  acceptance?: string[]
  regression?: string[]
  qc_checklist?: string[]
  [key: string]: unknown
}

/** `store.analysis_dict`. */
export interface IssueAnalysis {
  id: number
  version: number
  summary?: string | null
  root_cause?: string | null
  evidence: AnalysisEvidence[]
  affected_objects: AffectedObject[]
  plan: ChangePlan
  risk?: string | null
  risk_reasons: string[]
  confidence?: number | null
  test_plan: TestPlan
  created_at?: string | null
  /** Not served yet — the transcript button appears once it is. */
  agent_run_id?: number | null
}

export interface ChangedFile {
  path: string
  /** git name-status letter: A · M · D … */
  status?: string
}

export interface BuildTestRun {
  id: number
  /** lint · crud · bi · page · unit · coverage · acceptance · regression … */
  suite: string
  status: string
  summary?: string | null
  details: Record<string, unknown>
  started_at?: string | null
  finished_at?: string | null
}

/** `store.build_dict`. */
export interface IssueBuild {
  id: number
  attempt: number
  branch?: string | null
  status?: string | null
  base_commit?: string | null
  head_commit?: string | null
  staging_db?: string | null
  changed_files: ChangedFile[]
  diff_stats: { files?: number; insertions?: number; deletions?: number; [k: string]: unknown }
  correction_cycles?: number | null
  error?: string | null
  created_at?: string | null
  finished_at?: string | null
  tests: BuildTestRun[]
  /** Not served yet — the transcript button appears once it is. */
  agent_run_id?: number | null
}

export interface QcChecklistItem {
  item: string
  done: boolean
}

export interface QcReview {
  id: number
  verdict: 'pass' | 'fail' | string
  reviewer_id?: string | null
  reviewer_kind?: string | null
  checklist: QcChecklistItem[] | Record<string, unknown>[]
  notes?: string | null
  at?: string | null
}

/** One agent run of an issue's pipeline (team detail `agent_runs`). */
export interface AgentRunSummary {
  id: number
  stage: string
  status: string
  engine?: string | null
  model?: string | null
  error?: string | null
  started_at?: string | null
  finished_at?: string | null
}

/** `jobs.summary` — a pipeline stage run. */
export interface SupportJob {
  id: number
  kind: string
  /** queued · running · done · failed */
  state: string
  attempts: number
  last_error?: string | null
  created_at?: string | null
  finished_at?: string | null
}

/** `store.issue_detail` (+ `jobs` for the vendor team). */
export interface IssueDetail extends IssueSummary {
  description?: string | null
  reporter_email?: string | null
  reporter_kind?: string | null
  context: Record<string, unknown>
  duplicate_of?: { number: number; title: string; status: string } | null
  watchers: string[]
  ai_confidence?: number | null
  closed_at?: string | null
  deployment_ref?: string | null
  events: IssueEvent[]
  attachments: IssueAttachment[]
  my_capacities: string[]
  actions: IssueAction[]
  fixed_in_release?: { id: number; version: string; released_at?: string | null } | null
  // Vendor team only.
  analysis?: IssueAnalysis | null
  build?: IssueBuild | null
  qc_reviews?: QcReview[]
  pipeline?: Record<string, unknown>
  jobs?: SupportJob[]
  /** The stages that may be re-run in the issue's current state. */
  retry_stages?: RetryStage[]
  /** Every agent run of this issue's pipeline (transcripts). */
  agent_runs?: AgentRunSummary[]
  /** Set when a deployed runtime parked the report in its outbox (hub offline). */
  queued?: boolean
}

export interface IssueListQuery {
  /** `mine` (reported/watched) · `org` (my organization's) · `all` (team). */
  scope?: 'mine' | 'org' | 'all'
  status?: string[]
  type?: string
  severity?: string
  tenant?: string
  /** A user id, or `me`. */
  assignee?: string
  q?: string
  open?: boolean
  limit?: number
  offset?: number
}

/** A report a deployed runtime holds in its outbox (hub unreachable). */
export interface QueuedReport {
  queued: true
  outbox_id: number | string
  title?: string | null
  message?: string | null
}

/** One outbox row in a deployed runtime's issue list. */
export interface QueuedListItem {
  queued: true
  outbox_id: number | string
  title?: string | null
  type?: string | null
  severity?: string | null
  created_at?: string | null
  attempts?: number
  last_error?: string | null
}

export interface IssueListPage {
  issues: IssueSummary[]
  total: number
  /** Deployed runtime: the caller's reports not yet delivered to the hub. */
  queued?: QueuedListItem[]
}

/** Whether `createIssue` parked the report rather than filing it. */
export function isQueuedReport(x: unknown): x is QueuedReport {
  return !!x && typeof x === 'object' && (x as { queued?: unknown }).queued === true
    && 'outbox_id' in (x as object)
}

/** The auto-captured context sent with a report (`store._clean_context` keys). */
export interface IssueContext {
  screen?: string
  obj_name?: string
  obj_title?: string
  obj_type?: string
  record_id?: string
  form?: string
  url?: string
  route?: string
  browser?: string
  viewport?: string
  app_version?: string
  release?: string
  client_errors?: unknown[]
  failed_calls?: unknown[]
  locale?: string
  timezone?: string
  surface?: string
  steps?: string
  [key: string]: unknown
}

export interface NewIssueInput {
  title: string
  description?: string
  type?: string
  severity?: string
  context?: IssueContext
  labels?: string[]
  /** run · assistant · builder · agent */
  source?: string
}

export interface IssuePatch {
  priority?: string | null
  severity?: string
  issue_type?: string
  assignee_id?: string | null
  labels?: string[]
  title?: string
}

export interface QcInput {
  verdict: 'pass' | 'fail'
  checklist: QcChecklistItem[]
  notes?: string
}

export interface DiffResult {
  /** Without `path`: the build's changed files + stats. */
  files?: ChangedFile[]
  stats?: Record<string, unknown>
  /** With `path`: one file's unified diff. */
  path?: string
  diff?: string
}

/** Re-runnable pipeline stages (`RetryIn.stage`). */
export type RetryStage = 'triage' | 'analyse' | 'build' | 'test' | 'qa'

export interface AgentTranscript {
  id: number
  stage: string
  status: string
  engine?: string | null
  model?: string | null
  error?: string | null
  transcript: string
  input_tokens?: number | null
  output_tokens?: number | null
}

// ── policy ─────────────────────────────────────────────────────────────────

/** The effective policy (`policy.load`) — stored overrides over defaults. */
export interface SupportPolicyDoc {
  issue_types?: Array<SupportVocabItem & { fixable?: boolean; auto_analyse?: boolean }>
  severities?: Array<SupportVocabItem & { sla_hours?: number | null }>
  priorities?: string[]
  gates?: {
    auto_triage?: boolean
    auto_analyse?: string
    plan_approval?: string
    qc?: string
    pm_approval?: string
    auto_confidence_threshold?: number
    [key: string]: unknown
  }
  build?: {
    max_correction_cycles?: number
    max_concurrent_builds?: number
    staging_seed_rows?: number
    /** UI acceptance cases run in a browser on each fix's staging copy. */
    ui_tests?: { mode?: 'touched' | 'all' | 'off'; max_cases?: number; blocking?: boolean }
    [key: string]: unknown
  }
  /** Failures found by UI acceptance tests (backend `ui_testing/routing.py`). */
  from_tests?: {
    auto_send?: boolean
    auto_classifications?: string[]
    obvious_failure_codes?: string[]
    obvious_evidence?: string[]
    auto_severities?: string[]
    repeat_threshold?: number
    max_auto_sends?: number
    /** A failure from a run in someone's own browser: always a person's call, or routed as usual. */
    browser_runs?: 'person' | 'same'
    gates?: { plan_approval?: string; qc?: string; pm_approval?: string }
    [key: string]: unknown
  }
  release?: { mode?: string; notify_reporters?: boolean; [key: string]: unknown }
  github?: { mode?: string; target_branch?: string; create_release?: boolean; [key: string]: unknown }
  notifications?: { email_reporter?: boolean; email_func_admins?: boolean; [key: string]: unknown }
  environments?: { dev_db_split?: boolean; [key: string]: unknown }
  versioning_enabled?: boolean
  [key: string]: unknown
}

export interface PolicyResponse {
  policy: SupportPolicyDoc
  defaults?: SupportPolicyDoc
}

// ── releases ───────────────────────────────────────────────────────────────

/** One issue's line in the release notes. */
export interface ReleaseNote {
  issue_id?: number
  number: number
  type?: string
  title?: string
  note?: string
  /** New · Improved · Fixed */
  group?: string
  [key: string]: unknown
}

export interface ReleaseProgressEntry {
  at?: string
  step: string
  detail?: string
  [key: string]: unknown
}

/** `releases.release_dict`. */
export interface Release {
  id: number
  version: string
  /** major · minor · patch · initial */
  kind?: string | null
  /** draft · building · released · failed · withdrawn */
  status: string
  title?: string | null
  notes_md?: string | null
  notes: ReleaseNote[]
  tag?: string | null
  merge_commit?: string | null
  github_pr_url?: string | null
  github_release_url?: string | null
  /** pushed · skipped · failed … */
  push_status?: string | null
  push_detail?: string | null
  schema_changes: SchemaDiff | Record<string, unknown>
  progress: {
    step?: string
    log?: ReleaseProgressEntry[]
    admin_notes?: string[]
    [key: string]: unknown
  }
  error?: string | null
  has_bundle?: boolean
  created_at?: string | null
  released_at?: string | null
  issues?: IssueSummary[]
  jobs?: SupportJob[]
}

export interface ReleasesPage {
  releases: Release[]
  /** Team only: pm-approved issues waiting for a release. */
  ready: IssueSummary[]
}

export interface NewReleaseInput {
  issue_numbers?: number[]
  kind?: string
  version?: string
  title?: string
}

export interface ReleasePatch {
  version?: string
  kind?: string
  title?: string
  notes_md?: string
  notes?: ReleaseNote[]
}

// ── versioning + intake keys ───────────────────────────────────────────────

/** `start_versioning(dry_run=True)` — what Start versioning would do. */
export interface VersioningPreview {
  dry_run?: boolean
  already?: boolean
  version?: string | null
  /** `linked`, or a sentence saying the tag stays local until one is linked. */
  repository?: string
  /** `existing` · `will be created` */
  git_repo?: string
  uncommitted_changes?: boolean
  [key: string]: unknown
}

/**
 * Release management for an application whose LIVE copies run on their own
 * servers, from git (`model: "live_from_git"`): the platform is development
 * and test; a release is a pushed tag each live server upgrades itself from.
 */
export interface VersioningStatus {
  enabled: boolean
  model?: string
  deployments?: LiveDeployment[]
  latest?: string | null
  preview?: VersioningPreview
  preview_error?: string
}

export interface StartVersioningInput {
  version?: string
}

export interface StartVersioningResult {
  started?: boolean
  already?: boolean
  release?: { id: number; version: string; push_status?: string | null }
  version?: string | null
  [key: string]: unknown
}

// ── live deployments (a deployment = an intake key) ────────────────────────

/** How the AI reads a live deployment's data — through a READ-ONLY connection. */
export interface LiveDataConfig {
  /** A connection registered for the project (direct read-only, or a gateway). */
  connection_id?: string | null
  /** `per_tenant`: one database per tenant (twasta_preview_<tenant>);
   *  `fixed`: one shared database. */
  tenant_database?: 'per_tenant' | 'fixed' | string
  /** Tenant id → connection id, overriding `connection_id` for that tenant. */
  tenant_connections?: Record<string, string>
}

export interface LiveDeployment {
  /** The intake key's id. */
  id: string
  label?: string | null
  last_used_at?: string | null
  current_version?: string | null
  latest_version?: string | null
  behind?: boolean
  live_data?: LiveDataConfig
  git?: { branch?: string | null; notes?: string | null }
  /** Issues reported from this deployment. */
  issues?: number
}

export interface ProjectConnection {
  id: string
  name?: string | null
  type?: string | null
}

export interface DeploymentsPage {
  deployments: LiveDeployment[]
  connections: ProjectConnection[]
}

export interface DeploymentPatch {
  label?: string
  live_data?: LiveDataConfig
  git?: { branch?: string | null; notes?: string | null }
}

export interface IntakeKey {
  id: string
  label: string
  created_at?: string | null
  created_by?: string | null
  revoked_at?: string | null
  last_used_at?: string | null
}

/** A freshly minted key — `key` is shown ONCE and never served again. */
export interface MintedIntakeKey {
  id: string
  label: string
  key: string
  tenant_id?: string
}

// ── updates (an organization's functional administrator) ───────────────────

export interface SchemaColumnChange {
  table: string
  column: string
  /** added · removed · type */
  change: string
  type?: string
  from?: string
  to?: string
}

export interface SchemaDiff {
  tables_added?: string[]
  tables_removed?: string[]
  columns?: SchemaColumnChange[]
  destructive?: boolean
}

/** One release in the cumulative "what changes for you" list. */
export interface ReleaseChange {
  version: string
  title?: string | null
  notes: ReleaseNote[]
  notes_md?: string | null
  released_at?: string | null
  admin_notes: string[]
}

export interface UpgradeOutcome {
  ok?: boolean
  rollback?: boolean
  error?: string
  /** git_missing · auth · unreachable · tag_missing · not_on_branch · failed */
  error_kind?: string | null
  /** Where it was installed from: `git` (the release tag) or `bundle`. */
  source?: string | null
  tag?: string | null
  commit?: string | null
  note?: string | null
  from?: string | null
  to?: string | null
  finished_at?: string | null
  by?: string | null
  [key: string]: unknown
}

/** A version as the updates feed describes it — a full `Release` on the
 *  platform, fewer fields from a deployed runtime's feed. */
export type UpdateVersion = Partial<Release> & {
  version: string
  /** Deployed runtime: where the installed version is known from. */
  source?: string | null
  installed_at?: string | null
}

/** `distribution.org_status` + `enabled` + `changes`. */
export interface UpdatesStatus {
  enabled: boolean
  tenant_id?: string
  current?: UpdateVersion | null
  previous_release_id?: number | null
  available?: UpdateVersion[]
  latest?: string | null
  auto_upgrade?: string
  maintenance_window?: string | null
  upgrading?: boolean
  last_upgrade?: UpgradeOutcome | null
  changes?: ReleaseChange[]
  /** The running upgrade's current step (deployed runtime: downloading, …). */
  upgrade_step?: string | null
  /** The auto-upgrade modes this host offers (absent → the policy's list). */
  auto_upgrade_options?: string[]
  /** Deployed runtime: where an upgrade is taken from. */
  upgrade_source?: UpgradeSource | null
  pending_update?: PendingUpdate | null
}

/** Where a deployed runtime takes an upgrade from. */
export interface UpgradeSource {
  /** `git` — check out the release tag · `bundle` — the platform's bundle · `none` */
  kind: string
  /** The repository (credentials redacted). */
  repo_url?: string | null
  /** Where the address comes from: this server's settings, or the platform (`hub`). */
  repo_source?: string | null
  branch?: string | null
  token_configured?: boolean
}

export interface UpgradePreflight {
  from?: string | null
  to: string
  changes: ReleaseChange[]
  schema: SchemaDiff
  destructive: boolean
  overlay_conflicts: unknown[]
  /** Set when the upgrade is destructive: type it to confirm. */
  confirm_phrase?: string | null
  /** Deployed runtime: the release tag, whether it is pushed, and the source. */
  tag?: string | null
  pushed?: boolean | null
  source?: string | null
  repo_url?: string | null
}

export interface UpgradeInput {
  version: string
  confirm?: string
  tenant?: string
}

export interface UpgradeResult {
  queued?: boolean
  preflight?: UpgradePreflight
}

export interface RollbackResult {
  rolled_back_to: string
  note?: string | null
}

export interface UpdateSettingsInput {
  /** off · patch · minor */
  auto_upgrade: string
  maintenance_window?: string | null
  tenant?: string
}

export interface UpdateSettingsResult {
  auto_upgrade: string
  maintenance_window?: string | null
}

// ── the transport ──────────────────────────────────────────────────────────

/**
 * Everything the Support UI can do. The REPORTER half is required (every
 * surface that mounts the button must let somebody report and follow an
 * issue); everything else is optional, and a component whose method is absent
 * hides the control rather than offering a button that cannot work. The
 * downloaded runtime supplies the reporter half + updates; the platform
 * supplies all of it.
 */
export interface SupportRuntime {
  // reporter side — required
  meta(): Promise<SupportMeta>
  listIssues(query?: IssueListQuery): Promise<IssueListPage>
  getIssue(number: number): Promise<IssueDetail>
  /** A deployed runtime may answer `QueuedReport` (hub offline, kept in its outbox). */
  createIssue(input: NewIssueInput): Promise<IssueDetail | QueuedReport>
  transition(number: number, event: string, comment?: string): Promise<IssueDetail>
  comment(number: number, body: string, opts?: { internal?: boolean }): Promise<IssueDetail>
  counts?(): Promise<SupportCounts>
  watch?(number: number, on: boolean): Promise<{ watching: boolean }>
  uploadAttachment?(number: number, file: Blob, filename?: string): Promise<IssueAttachment>
  /** Fetch an attachment's bytes (works on bearer-token surfaces). */
  downloadAttachment?(number: number, attachmentId: number): Promise<Blob>
  /** A same-origin URL for an attachment (cookie-authenticated surfaces). */
  attachmentUrl?(number: number, attachmentId: number): string
  /** Where the caller is (`?env=stage:<n>` resolves to that fix's staging). */
  environment?(env?: string): Promise<SupportEnvironment>

  // team side
  patchIssue?(number: number, patch: IssuePatch): Promise<{ changed: string[]; issue: IssueDetail }>
  qc?(number: number, input: QcInput): Promise<IssueDetail>
  diff?(number: number, path?: string): Promise<DiffResult>
  retry?(number: number, stage: RetryStage): Promise<{ queued: number }>
  transcript?(runId: number): Promise<AgentTranscript>
  getPolicy?(): Promise<PolicyResponse>
  putPolicy?(patch: SupportPolicyDoc): Promise<{ policy: SupportPolicyDoc }>

  // releases
  listReleases?(): Promise<ReleasesPage>
  createRelease?(input: NewReleaseInput): Promise<Release>
  getRelease?(id: number): Promise<Release>
  patchRelease?(id: number, patch: ReleasePatch): Promise<Release>
  cutRelease?(id: number): Promise<Release>
  discardRelease?(id: number): Promise<{ discarded: boolean }>

  // versioning + live deployments + intake keys
  versioningStatus?(): Promise<VersioningStatus>
  listDeployments?(): Promise<DeploymentsPage>
  configureDeployment?(id: string, patch: DeploymentPatch): Promise<LiveDeployment>
  startVersioning?(input: StartVersioningInput): Promise<StartVersioningResult>
  listIntakeKeys?(): Promise<{ keys: IntakeKey[] }>
  mintIntakeKey?(label: string): Promise<MintedIntakeKey>
  revokeIntakeKey?(id: string): Promise<{ revoked: boolean }>

  // updates — a deployed runtime upgrading itself (the platform serves none:
  // live servers upgrade from the release's git tag)
  updates?(tenant?: string): Promise<UpdatesStatus>
  preflight?(version: string, tenant?: string): Promise<UpgradePreflight>
  upgrade?(input: UpgradeInput): Promise<UpgradeResult>
  /** Ask the platform now whether a newer release is out. */
  checkUpdates?(): Promise<{ pending_update: PendingUpdate | null }>
  rollback?(tenant?: string): Promise<RollbackResult>
  updateSettings?(input: UpdateSettingsInput): Promise<UpdateSettingsResult>
}

/** Host toast adapter (same shape as the shell's `ShellNotify`). */
export interface SupportNotify {
  success(message: string): void
  error(message: string): void
}
